import { ChangeDetectionStrategy, Component, computed, input, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatNoDataRow,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoDirective } from '@jsverse/transloco';
import { AraDecimalFormatDirective } from '../../../directives/ara-decimal-format.directive';

/** Every row backs its editable cells with a FormGroup keyed by the column `key`s. */
export interface AraInlineEditRow {
	group: FormGroup;
}

/** One table column. Editable input by default; a display column when `cellTemplate` is set. */
export interface AraInlineEditColumn {
	/** FormControl name inside the row's group (editable columns); also the column id. */
	key: string;
	/** Column header — a transloco key (translated by the table). */
	header: string;
	/** Optional unit suffix rendered inside the field (literal, e.g. '€', '%'). */
	suffix?: string;
	/** Optional fixed column width, e.g. '160px'. */
	width?: string;
	/** Format as decimal (araDecimalFormat + inputmode="decimal"); otherwise a plain text input. */
	decimal?: boolean;
	/** Value bounds of a `decimal` column, passed to `araDecimalFormat` — it clamps the typed value to the bound (e.g. */
	minValue?: number;
	maxValue?: number;
	/** When set, the cell renders this template (context `{ $implicit: row, column }`) instead of an editable input. */
	cellTemplate?: TemplateRef<unknown>;
}

/**
 * Generic column-driven inline-edit table — a lean FormGroup-per-row grid for editing a handful of
 * fields directly in the table, NOT the heavy search/pager `ara-generic-table`. Editable columns are
 * config (`columns`); an optional leading (label) column and trailing action column are consumer
 * templates. The consumer builds the FormGroups and wires any cross-field logic.
 */
@Component({
	selector: 'ara-inline-edit-table',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgTemplateOutlet,
		ReactiveFormsModule,
		TranslocoDirective,
		MatTable,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderCellDef,
		MatCell,
		MatCellDef,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRow,
		MatRowDef,
		MatNoDataRow,
		MatFormField,
		MatInput,
		MatSuffix,
		AraDecimalFormatDirective,
	],
	templateUrl: './ara-inline-edit-table.component.html',
	styleUrl: './ara-inline-edit-table.component.scss',
})
export class AraInlineEditTableComponent<T extends AraInlineEditRow = AraInlineEditRow> {
	readonly rows = input<T[]>([]);
	readonly columns = input<AraInlineEditColumn[]>([]);
	/** Header of the optional leading column — a transloco key. */
	readonly leadingHeader = input('');
	/** Optional leading (non-editable / label) cell per row; context `{ $implicit: row }`. */
	readonly leadingTemplate = input<TemplateRef<{ $implicit: T }> | null>(null);
	/** Optional trailing action cell; context `{ $implicit: row, index }`. Omit for a read-only table. */
	readonly actionTemplate = input<TemplateRef<{ $implicit: T; index: number }> | null>(null);
	/** No-data row text — a transloco key. */
	readonly emptyText = input('');
	/** Content of the canonical add row at the table's end — a template, so callers can offer several actions. */
	readonly addRowTemplate = input<TemplateRef<unknown> | null>(null);
	/** Full-width context line above the table (e.g. the preceding role of a time strand). */
	readonly beforeTableTemplate = input<TemplateRef<unknown> | null>(null);
	/** Full-width context line below the table, above the add row. */
	readonly afterTableTemplate = input<TemplateRef<unknown> | null>(null);

	protected readonly displayedColumns = computed<string[]>(() => [
		...(this.leadingTemplate() ? ['__leading'] : []),
		...this.columns().map((column) => column.key),
		...(this.actionTemplate() ? ['__actions'] : []),
	]);

	protected readonly trackRow = (_: number, row: T): FormGroup => row.group;
}
